import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import {
    PlayCircle,
    Music2,
    Tv,
    Film,
    Truck,
    FileText,
    BarChart3,
    Video,
    ShieldCheck,
    Layers,
    Headset,
    Search,
    ClipboardList,
    Rocket,
    ArrowRight,
    Check,
    Volume2,
    Package,
    ChevronDown,
    PhoneCall,
    Clock,
    BadgeCheck
} from 'lucide-react';

/* =========================
   PLATFORM DATA
   "channel" = the preset number shown on each card, the
   way a channel guide numbers its presets.
========================= */
const PLATFORMS = [
    { id: "prime", channel: "01", name: "Amazon Prime Video", category: "Streaming & OTT", desc: "In-stream video ads shown before, during and between Prime Video content.", icon: <PlayCircle size={22} />, highlight: "Best for premium, binge-watch attention", surface: "screen" },
    { id: "spotify", channel: "02", name: "Spotify", category: "Streaming & OTT", desc: "Audio ads and companion banners reaching music and podcast listeners.", icon: <Music2 size={22} />, highlight: "Best for reaching commuters & multitaskers", surface: "audio" },
    { id: "zee5", channel: "03", name: "ZEE5", category: "Streaming & OTT", desc: "Video ad placements across ZEE5's regional and national catalogue.", icon: <Tv size={22} />, highlight: "Best for deep regional-language reach", surface: "screen" },
    { id: "hotstar", channel: "04", name: "Disney+ Hotstar", category: "Streaming & OTT", desc: "Ad slots across sports, cinema and entertainment content.", icon: <Film size={22} />, highlight: "Best for big-event, high-emotion moments", surface: "screen" },
    { id: "mx", channel: "05", name: "MX Player", category: "Streaming & OTT", desc: "High-frequency video ads across a large regional viewer base.", icon: <Video size={22} />, highlight: "Best for high-frequency repeat impressions", surface: "screen" },
    { id: "courier", channel: "06", name: "Amazon Courier Ads", category: "Amazon Ecosystem", desc: "Your brand on Amazon delivery packaging, seen at the customer's door.", icon: <Truck size={22} />, highlight: "Best for the last, most memorable touchpoint", surface: "doorstep" },
    { id: "pamphlet", channel: "07", name: "Amazon Pamphlet Ads", category: "Amazon Ecosystem", desc: "Physical inserts placed inside Amazon parcels for direct recall.", icon: <FileText size={22} />, highlight: "Best for in-hand offers & coupon recall", surface: "doorstep" },
    { id: "dsp", channel: "08", name: "Amazon DSP", category: "Amazon Ecosystem", desc: "Programmatic display, video and audio bought via Amazon's DSP.", icon: <BarChart3 size={22} />, highlight: "Best for retargeting shoppers, data-first", surface: "audio" }
];

const CATEGORIES = ["All Platforms", "Streaming & OTT", "Amazon Ecosystem"];

const DEVICE_TABS = [
    { id: "screen", label: "OTT Screen", icon: <Tv size={15} />, frame: "tv" },
    { id: "audio", label: "Audio & Data", icon: <Volume2 size={15} />, frame: "phone" },
    { id: "doorstep", label: "Doorstep", icon: <Package size={15} />, frame: "parcel" }
];

const FAQS = [
    { q: "Do I need a huge budget to start?", a: "No. Budgets vary by platform and goal — during your consultation we'll match a starting budget to your category, then show you what results look like at that level before you commit." },
    { q: "Do you also make the ad creative, or just run it?", a: "We handle both. Our team can produce the video, audio or image creative for your campaign, or adapt assets you already have to fit each platform's specs." },
    { q: "How do I know the campaign is actually working?", a: "You get regular performance reports from your single point of contact, covering what ran, where, and how it performed — no need to chase multiple platform dashboards yourself." },
    { q: "Is Amazon Courier & Pamphlet advertising real, or a gimmick?", a: "It's a genuine Amazon ad product — your brand is printed directly onto delivery packaging or inserted inside parcels, so it's seen at the moment a customer is most engaged: opening their order." },
    { q: "How fast can we go live?", a: "Timelines depend on the platforms you choose and creative readiness. We'll give you a realistic go-live date during your consultation, based on your current assets and chosen platform mix." }
];

const PROCESS_STEPS = [
    { n: "01", title: "Understand Your Audience", detail: "We review your business, category and budget to shortlist platforms likely to reach real customers.", icon: <Search size={50} /> },
    { n: "02", title: "Plan the Campaign", detail: "We prepare creatives, set targeting and budgets, and get everything approved on the right ad accounts.", icon: <ClipboardList size={50} /> },
    { n: "03", title: "Launch & Optimize", detail: "Your campaign goes live, and we track performance daily — shifting spend toward what's working.", icon: <Rocket size={50} /> }
];

const BENEFITS = [
    { title: "Registered Advertising Partner", desc: "Authorized to plan and run campaigns directly on Amazon and major OTT platforms — no middlemen.", icon: <ShieldCheck size={19} /> },
    { title: "One Team, Every Platform", desc: "A single point of contact manages your campaign across all 8 platforms instead of separate vendors.", icon: <Layers size={19} /> },
    { title: "Ongoing Support", desc: "Regular performance reports and a dedicated contact for any changes to your campaign.", icon: <Headset size={19} /> }
];

const TICKER_MARKS = [
    { Icon: PlayCircle, label: "PRIME VIDEO" }, { Icon: Music2, label: "SPOTIFY" }, { Icon: Tv, label: "ZEE5" },
    { Icon: Film, label: "HOTSTAR" }, { Icon: Video, label: "MX PLAYER" }, { Icon: Truck, label: "AMAZON COURIER" },
    { Icon: FileText, label: "AMAZON PAMPHLET" }, { Icon: BarChart3, label: "AMAZON DSP" }
];

/* =========================
   SCROLL-REVEAL HOOK
========================= */
const useInView = (threshold = 0.2) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setInView(true); observer.disconnect(); }
        }, { threshold });
        observer.observe(node);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return [ref, inView];
};

/* =========================
   LIVE TICKER — the page's signature mark.
   A broadcast-style crawl announcing every channel
   the campaign can reach, with a pulsing ON AIR dot.
========================= */
const LiveTicker = () => {
    const items = [...TICKER_MARKS, ...TICKER_MARKS];
    return (
        <div className="ottz-ticker" role="presentation" aria-hidden="true">
            <span className="ottz-ticker-live"><i /> ON AIR</span>
            <div className="ottz-ticker-track">
                {items.map(({ Icon, label }, i) => (
                    <span className="ottz-ticker-item" key={i}><Icon size={13} />{label}</span>
                ))}
            </div>
        </div>
    );
};

/* =========================
   SIGNAL DIVIDER — a tear-free section break styled like
   the frequency ticks on a tuner dial.
========================= */
const SignalDivider = () => <div className="ottz-divider" role="presentation" />;

/* =========================
   CTA BANNER — a direct, no-form nudge to the contact page.
========================= */
const CtaBanner = ({ eyebrow, title, subtitle }) => {
    return (
        <div className="ottz-ctaband">
            <div className="ottz-ctaband-inner">
                <div className="ottz-ctaband-copy">
                    {eyebrow && <span className="ottz-eyebrow">{eyebrow}</span>}
                    <h3>{title}</h3>
                    {subtitle && <p>{subtitle}</p>}
                </div>
                <Link to="/contact" className="ottz-btn-primary">
                    <span>Talk to Us</span> <ArrowRight size={18} />
                </Link>
            </div>
        </div>
    );
};

/* =========================
   COUNT-UP STAT
========================= */
const StatBlock = ({ value, suffix = "", label, icon }) => {
    const [ref, inView] = useInView(0.4);
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!inView) return;
        let start = null;
        const duration = 1100;
        const step = (ts) => {
            if (!start) start = ts;
            const progress = Math.min((ts - start) / duration, 1);
            setCount(Math.floor(progress * value));
            if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [inView, value]);
    const pct = inView ? (count / value) * 100 : 0;
    return (
        <div className="ottz-stat" ref={ref}>
            <div className="ottz-stat-ring" style={{ '--pct': pct }}>
                <div className="ottz-stat-ring-inner">
                    {icon}
                    <div className="ottz-stat-num">{count}{suffix}</div>
                </div>
            </div>
            <div className="ottz-stat-label">{label}</div>
        </div>
    );
};

/* =========================
   INTERACTIVE DEVICE SHOWCASE
   Lets the visitor flip between the three physical/digital
   surfaces an ad can actually land on.
========================= */
const DeviceShowcase = () => {
    const [active, setActive] = useState("screen");
    const [ref, inView] = useInView(0.3);
    const tab = DEVICE_TABS.find(t => t.id === active);
    const matches = PLATFORMS.filter(p => p.surface === active);

    return (
        <section className="ottz-device" id="preview" ref={ref}>
            <div className="ottz-section-head">
                <span className="ottz-eyebrow">Proof of placement</span>
                <h2>See Where Your Ad Actually Lands</h2>
                <p>Tap a surface below — the same brand slot, shown the way a viewer, listener or customer would see it.</p>
            </div>

            <div className="ottz-device-tabs">
                {DEVICE_TABS.map(t => (
                    <button
                        key={t.id}
                        className={`ottz-device-tab ${active === t.id ? 'active' : ''}`}
                        onClick={() => setActive(t.id)}
                    >
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            <div className={`ottz-device-stage ${inView ? 'in-view' : ''}`}>
                <div className={`ottz-device-frame frame-${tab.frame}`} key={tab.id}>
                    {tab.frame === 'tv' && (
                        <div className="ottz-frame-body tv">
                            <div className="ottz-frame-bar">
                                <span>ON AIR</span>
                            </div>
                            <div className="ottz-frame-screen">
                                <div className="ottz-ad-pill">Ad Break</div>
                                <div className="ottz-ad-slot">Your Brand Here</div>
                            </div>
                            <div className="ottz-frame-stand" />
                        </div>
                    )}
                    {tab.frame === 'phone' && (
                        <div className="ottz-frame-body phone">
                            <div className="ottz-frame-notch" />
                            <div className="ottz-frame-screen">
                                <div className="ottz-audio-wave">
                                    {Array.from({ length: 9 }).map((_, i) => <span key={i} style={{ animationDelay: `${i * 0.08}s` }} />)}
                                </div>
                                <div className="ottz-ad-pill">Sponsored</div>
                                <div className="ottz-ad-slot">Your Brand Here</div>
                            </div>
                        </div>
                    )}
                    {tab.frame === 'parcel' && (
                        <div className="ottz-frame-body parcel">
                            <div className="ottz-frame-screen parcel-face">
                                <Package size={30} className="ottz-parcel-icon" />
                                <div className="ottz-ad-pill dark">On the packaging</div>
                                <div className="ottz-ad-slot dark">Your Brand Here</div>
                            </div>
                            <div className="ottz-parcel-badge"><span className="ottz-parcel-dot" />At the customer's door</div>
                        </div>
                    )}
                </div>

                <div className="ottz-device-copy">
                    <span className="ottz-device-kicker">{tab.label}</span>
                    <h3>{active === 'screen' && "Front and center during the content people chose."}
                        {active === 'audio' && "Heard and seen between songs, podcasts and shopping data."}
                        {active === 'doorstep' && "Held in hand at the single most attentive moment: unboxing."}</h3>
                    <ul className="ottz-device-list">
                        {matches.map(p => (
                            <li key={p.id}><span className="ottz-device-list-code">{p.channel}</span>{p.icon}<span>{p.name}</span></li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
};

/* =========================
   FAQ ACCORDION — removes hesitation before the ask
========================= */
const FaqAccordion = () => {
    const [open, setOpen] = useState(0);
    return (
        <div className="ottz-faq-list">
            {FAQS.map((f, i) => (
                <div className={`ottz-faq-item ${open === i ? 'open' : ''}`} key={f.q}>
                    <button className="ottz-faq-q" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i}>
                        <span className="ottz-faq-num">Q{String(i + 1).padStart(2, '0')}</span>
                        <span className="ottz-faq-qtext">{f.q}</span>
                        <ChevronDown size={18} className="ottz-faq-chevron" />
                    </button>
                    <div className="ottz-faq-a"><p>{f.a}</p></div>
                </div>
            ))}
        </div>
    );
};

/* =========================
   CONSULTATION PANEL — a direct hand-off to the contact page.
========================= */
const ConsultationPanel = () => {
    return (
        <div className="ottz-consult">
            <div className="ottz-consult-icon"><PhoneCall size={22} /></div>
            <h4>Let's talk about your brand</h4>
            <p>Tell us your category and budget on the contact page — we'll map out the right platform mix and get back to you personally.</p>
            <Link to="/contact" className="ottz-btn-primary ottz-consult-btn">
                <span>Request a Consultation</span> <ArrowRight size={17} />
            </Link>
            <p className="ottz-lead-microcopy"><Clock size={12} /> One quick call — no obligation, no spam.</p>
        </div>
    );
};

export const OTTAdvertising = () => {
    const [activeCategory, setActiveCategory] = useState("All Platforms");
    const [gridRef, gridInView] = useInView(0.1);
    const [stepsRef, stepsInView] = useInView(0.2);
    const [benefitsRef, benefitsInView] = useInView(0.2);
    const heroRef = useRef(null);

    const handleHeroMove = (e) => {
        const node = heroRef.current;
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
        const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
        node.style.setProperty('--mx', x.toFixed(3));
        node.style.setProperty('--my', y.toFixed(3));
    };

    const visiblePlatforms = activeCategory === "All Platforms"
        ? PLATFORMS
        : PLATFORMS.filter(p => p.category === activeCategory);

    const handleCardTilt = (e) => {
        const card = e.currentTarget;
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        card.style.setProperty('--rx', (y * -4).toFixed(2));
        card.style.setProperty('--ry', (x * 4).toFixed(2));
    };
    const resetCardTilt = (e) => {
        e.currentTarget.style.setProperty('--rx', 0);
        e.currentTarget.style.setProperty('--ry', 0);
    };

    const pageStyles = `
        .ottz-page {
            --ink: #0E1B18; --ink-soft: #55645F; --ink-faint: #8B968F;
            --paper: #F3F6F2; --panel: #E7EDE6;
            --line: rgba(14,27,24,0.16); --line-soft: rgba(14,27,24,0.09);
            --signal: #E8622C; --signal-deep: #B8481C;
            --teal: #1F8F7A; --teal-deep: #146354;
            --violet: #7C5CC4; --violet-deep: #5B3F97;
            --gold: #C99A3D; --surface: #0F211D; --surface-deep: #081512;
            font-family: 'Inter', sans-serif; color: var(--ink); overflow-x: hidden; background: var(--paper);
        }
        .ottz-page, .ottz-page *, .ottz-page *::before, .ottz-page *::after { box-sizing: border-box; }
        .ottz-page img, .ottz-page svg { max-width: 100%; }
        .ottz-page .header-fixed { position: fixed; top: 0; left: 0; width: 100%; z-index: 9999; background: rgba(243,246,242,0.94); backdrop-filter: blur(10px); box-shadow: 0 1px 0 var(--line); }
        .ottz-page h1, .ottz-page h2, .ottz-page h3, .ottz-page .ottz-step-n { font-family: 'Bricolage Grotesque', 'Inter', sans-serif; letter-spacing: -0.01em; }
        .ottz-page .ottz-mono { font-family: 'IBM Plex Mono', monospace; }

        /* ---------- SHARED ---------- */
        .ottz-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-family: 'IBM Plex Mono', monospace; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: var(--signal-deep); margin-bottom: 12px; }
        .ottz-eyebrow::before { content: ''; width: 16px; height: 1px; background: var(--signal); }

        /* ---------- SIGNAL DIVIDER ---------- */
        .ottz-divider { height: 26px; background: var(--paper); position: relative;
            background-image: repeating-linear-gradient(90deg, var(--line) 0 1px, transparent 1px 16px);
            background-position: center; }
        .ottz-divider::before { content: ''; position: absolute; top: 50%; left: 0; right: 0; border-top: 1px solid var(--line-soft); }

        /* ---------- LIVE TICKER ---------- */
        .ottz-ticker { position: relative; z-index: 2; display: flex; align-items: center; gap: 14px;
            background: var(--surface-deep); border-radius: 999px; padding: 7px 8px 7px 14px; margin: 0 auto 34px;
            max-width: 100%; width: fit-content; overflow: hidden; border: 1px solid rgba(243,246,242,0.1); }
        .ottz-ticker-live { display: inline-flex; align-items: center; gap: 6px; font-family: 'IBM Plex Mono', monospace;
            font-size: 0.66rem; font-weight: 700; letter-spacing: 0.1em; color: var(--paper); flex-shrink: 0; }
        .ottz-ticker-live i { width: 6px; height: 6px; border-radius: 50%; background: var(--signal); display: inline-block; animation: ottz-pulse 1.6s ease-in-out infinite; }
        @keyframes ottz-pulse { 0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(232,98,44,0.5); } 50% { opacity: 0.55; box-shadow: 0 0 0 4px rgba(232,98,44,0); } }
        .ottz-ticker-track { display: flex; gap: 22px; white-space: nowrap; animation: ottz-scroll 22s linear infinite; width: max-content; }
        .ottz-ticker-item { display: inline-flex; align-items: center; gap: 6px; font-family: 'IBM Plex Mono', monospace; font-size: 0.68rem; font-weight: 600; letter-spacing: 0.05em; color: rgba(243,246,242,0.72); }
        @keyframes ottz-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @media (prefers-reduced-motion: reduce) { .ottz-ticker-track { animation: none; } }

        /* ---------- HERO ---------- */
        .ottz-hero {
            position: relative; overflow: hidden; isolation: isolate;
            background: var(--panel);
            background-image: radial-gradient(circle at 1.5px 1.5px, rgba(14,27,24,0.05) 1.5px, transparent 0);
            background-size: 26px 26px;
            padding: 130px 6% 64px; text-align: center; color: var(--ink);
            --mx: 0; --my: 0; border-bottom: 1px solid var(--line);
        }
        .ottz-hero-badge { position: relative; z-index: 2; display: inline-flex; align-items: center; gap: 7px;
            background: var(--paper); border: 1.5px solid var(--line); color: var(--ink-soft);
            font-family: 'IBM Plex Mono', monospace; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
            padding: 7px 14px; border-radius: 999px; margin-bottom: 22px; }
        .ottz-hero-badge svg { color: var(--teal-deep); }
        .ottz-hero h1 { position: relative; z-index: 2; font-size: clamp(1.6rem, 5.2vw, 3rem); font-weight: 700; line-height: 1.28; max-width: 780px; margin: 0 auto 18px; }
        .ottz-hero h1 .ottz-highlight { position: relative; display: inline-block; color: var(--paper); font-style: normal;
            background: var(--signal); padding: 1px 10px; border-radius: 4px; transform: rotate(-1deg); }
        .ottz-hero p { position: relative; z-index: 2; color: var(--ink-soft); font-size: clamp(0.9rem, 2.2vw, 1.1rem); line-height: 1.7; max-width: 600px; margin: 0 auto 30px; }
        .ottz-hero-actions { position: relative; z-index: 2; display: flex; flex-direction: column; gap: 16px; align-items: center; }
        .ottz-btn-primary {
            position: relative;
            background: var(--ink); color: var(--paper); font-weight: 700; text-decoration: none;
            padding: 15px 30px; border-radius: 8px; font-size: 0.92rem; display: inline-flex;
            align-items: center; gap: 8px; transition: transform 0.15s ease, background 0.2s ease;
            box-shadow: 3px 3px 0 var(--signal);
        }
        .ottz-btn-primary:hover { transform: translate(1px, 1px); box-shadow: 2px 2px 0 var(--signal); background: var(--surface-deep); }
        .ottz-btn-primary:active { transform: translate(3px, 3px); box-shadow: 0 0 0 var(--signal); }
        .ottz-btn-primary:disabled { cursor: default; }
        .ottz-btn-secondary { color: var(--ink); text-decoration: none; font-weight: 600; font-size: 0.85rem; font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.03em; border-bottom: 1px dashed var(--ink-faint); padding-bottom: 3px; }
        .ottz-btn-secondary:hover { border-color: var(--ink); }

        @media (min-width: 768px) {
            .ottz-hero { padding: 168px 8% 84px; }
            .ottz-hero-actions { flex-direction: row; gap: 26px; }
        }
        @media (max-width: 380px) {
            .ottz-hero { padding: 118px 5% 52px; }
            .ottz-hero-badge { font-size: 0.6rem; padding: 6px 11px; text-align: center; }
        }

        /* ---------- PLATFORMS / BUILDER ---------- */
        .ottz-platforms { padding: 60px 5% 40px; background: var(--paper); }
        .ottz-section-head { text-align: center; max-width: 660px; margin: 0 auto 14px; }
        .ottz-section-head h2 { font-size: clamp(1.4rem, 4vw, 1.8rem); font-weight: 700; color: var(--ink); margin-bottom: 10px; }
        .ottz-section-head p { color: var(--ink-soft); font-size: 1rem; line-height: 1.6; }

        .ottz-tabs { display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; margin-bottom: 36px; }
        .ottz-tab {
            background: var(--paper); border: 1.5px solid var(--line); color: var(--ink-soft);
            padding: 9px 18px; border-radius: 999px; font-size: 0.83rem; font-weight: 600;
            cursor: pointer; transition: all 0.2s ease;
        }
        .ottz-tab.active { background: var(--ink); border-color: var(--ink); color: var(--paper); }
        .ottz-tab:hover:not(.active) { border-color: var(--ink); color: var(--ink); }

        .ottz-grid { display: grid; grid-template-columns: 1fr; gap: 20px; max-width: 1200px; margin: 0 auto 40px; perspective: 1200px; }
        .ottz-card {
            position: relative; background: var(--paper); border-radius: 12px; padding: 22px 20px 20px;
            border: 1.5px solid var(--line); text-align: left;
            opacity: 0; transform: translateY(20px); overflow: hidden;
            --rx: 0; --ry: 0; border-left: 4px solid var(--line);
            transition: opacity 0.5s ease, transform 0.15s ease-out, box-shadow 0.3s ease, border-color 0.3s ease;
            transform-style: preserve-3d; will-change: transform;
        }
        .ottz-card.streaming { border-left-color: var(--teal); }
        .ottz-card.amazon { border-left-color: var(--signal); }
        .ottz-grid.in-view .ottz-card { opacity: 1; transform: translateY(0) rotateX(calc(var(--rx) * 1deg)) rotateY(calc(var(--ry) * 1deg)); }
        .ottz-card:hover { box-shadow: 6px 6px 0 var(--line-soft); transform: translateY(-3px) rotateX(calc(var(--rx) * 1deg)) rotateY(calc(var(--ry) * 1deg)); }

        .ottz-card-tag {
            position: absolute; top: 16px; left: -4px; font-family: 'IBM Plex Mono', monospace; font-size: 0.62rem; font-weight: 700;
            letter-spacing: 0.08em; color: var(--paper); padding: 2px 8px 2px 10px; border-radius: 0 999px 999px 0;
        }
        .ottz-card-tag.streaming { background: var(--teal-deep); }
        .ottz-card-tag.amazon { background: var(--signal-deep); }
        .ottz-card-top { display: flex; align-items: flex-start; gap: 13px; margin-bottom: 12px; margin-top: 6px; }
        .ottz-card-icon { width: 42px; height: 42px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: transform 0.3s ease; }
        .ottz-card:hover .ottz-card-icon { transform: scale(1.06) rotate(-3deg); }
        .ottz-card-icon.streaming { background: rgba(31,143,122,0.12); color: var(--teal-deep); }
        .ottz-card-icon.amazon { background: rgba(232,98,44,0.1); color: var(--signal-deep); }
        .ottz-card h3 { font-size: 1rem; font-weight: 700; color: var(--ink); margin-bottom: 4px; }
        .ottz-card-badge { display: inline-block; font-family: 'IBM Plex Mono', monospace; font-size: 0.63rem; font-weight: 700; letter-spacing: 0.06em; padding: 2px 0; text-transform: uppercase; }
        .ottz-card-badge.streaming { color: var(--teal-deep); }
        .ottz-card-badge.amazon { color: var(--signal-deep); }
        .ottz-card p { color: var(--ink-soft); font-size: 0.88rem; line-height: 1.55; }

        @media (min-width: 640px) { .ottz-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (min-width: 1100px) { .ottz-grid { grid-template-columns: repeat(4, 1fr); } }

        /* ---------- STATS ---------- */
        .ottz-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 30px; padding: 20px 6% 66px; max-width: 900px; margin: 0 auto; text-align: center; background: var(--paper); }
        .ottz-stat { display: flex; flex-direction: column; align-items: center; gap: 12px; }
        .ottz-stat-ring {
            width: 88px; height: 88px; border-radius: 50%; padding: 5px;
            background: conic-gradient(var(--ink) calc(var(--pct, 0) * 1%), var(--line-soft) 0);
            transition: background 0.15s linear;
        }
        .ottz-stat-ring-inner {
            width: 100%; height: 100%; border-radius: 50%; background: var(--paper);
            display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
            color: var(--ink);
        }
        .ottz-stat-num { font-family: 'IBM Plex Mono', monospace; font-size: 1.25rem; font-weight: 700; color: var(--ink); }
        .ottz-stat-label { font-size: 0.8rem; color: var(--ink-soft); }
        @media (min-width: 768px) { .ottz-stats { grid-template-columns: repeat(4, 1fr); } .ottz-stat-ring { width: 104px; height: 104px; } .ottz-stat-num { font-size: 1.45rem; } }
        @media (max-width: 360px) { .ottz-stats { grid-template-columns: repeat(2, 1fr); gap: 18px; } .ottz-stat-ring { width: 72px; height: 72px; } }

        /* ---------- PROCESS ---------- */
        .ottz-process { padding: 74px 6%; background: var(--panel); border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }
        .ottz-steps { display: grid; grid-template-columns: 1fr; gap: 0; max-width: 1100px; margin: 0 auto; position: relative; }
        .ottz-step { background: var(--paper); border: 1.5px solid var(--line); border-radius: 12px; padding: 26px 24px; opacity: 0; transform: translateY(20px); transition: all 0.55s cubic-bezier(0.16,1,0.3,1); position: relative; }
        .ottz-steps.in-view .ottz-step { opacity: 1; transform: translateY(0); }
        .ottz-steps.in-view .ottz-step:nth-child(2) { transition-delay: 0.1s; }
        .ottz-steps.in-view .ottz-step:nth-child(3) { transition-delay: 0.2s; }
        .ottz-step + .ottz-step { margin-top: 22px; }
        .ottz-step + .ottz-step::before {
            content: ''; position: absolute; left: 34px; top: -22px; width: 1px; height: 22px; border-left: 1.5px solid var(--line); }
        .ottz-step-top { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
        .ottz-step-n { font-family: 'IBM Plex Mono', monospace; width: 34px; height: 34px; border-radius: 50%; border: 1.5px solid var(--ink); color: var(--ink); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.82rem; flex-shrink: 0; }
        .ottz-step-icon { color: var(--signal-deep); display: flex; align-items: center; }
        .ottz-step h3 { font-size: 1.06rem; font-weight: 700; color: var(--ink); margin-bottom: 6px; }
        .ottz-step p { color: var(--ink-soft); font-size: 0.89rem; line-height: 1.6; }
        @media (min-width: 900px) {
            .ottz-steps { grid-template-columns: repeat(3, 1fr); gap: 24px; }
            .ottz-step + .ottz-step { margin-top: 0; }
            .ottz-step + .ottz-step::before { left: -22px; top: 34px; width: 22px; height: 1px; border-left: none; border-top: 1.5px solid var(--line); }
        }

        /* ---------- BENEFITS ---------- */
        .ottz-benefits { padding: 74px 6% 80px; background: var(--paper); }
        .ottz-benefits-grid { display: grid; grid-template-columns: 1fr; gap: 18px; max-width: 1100px; margin: 0 auto; }
        .ottz-benefit { display: flex; gap: 16px; padding: 22px; border-radius: 12px; background: var(--panel); border: 1px solid var(--line-soft); opacity: 0; transform: translateY(16px); transition: all 0.5s ease; }
        .ottz-benefits-grid.in-view .ottz-benefit { opacity: 1; transform: translateY(0); }
        .ottz-benefit-icon { width: 40px; height: 40px; border-radius: 8px; background: var(--paper); border: 1.5px solid var(--signal); color: var(--signal-deep); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .ottz-benefit h4 { font-size: 0.98rem; font-weight: 700; color: var(--ink); margin-bottom: 5px; }
        .ottz-benefit p { color: var(--ink-soft); font-size: 0.87rem; line-height: 1.55; }
        @media (min-width: 900px) { .ottz-benefits-grid { grid-template-columns: repeat(3, 1fr); } }

        /* ---------- CARD HIGHLIGHT CHIP ---------- */
        .ottz-card-highlight {
            display: flex; align-items: center; gap: 6px; margin-top: 12px; padding-top: 12px;
            border-top: 1px solid var(--line-soft); font-family: 'IBM Plex Mono', monospace; font-size: 0.72rem; font-weight: 600; color: var(--teal-deep);
            opacity: 0; max-height: 0; overflow: hidden; transition: opacity 0.3s ease, max-height 0.3s ease, padding 0.3s ease;
        }
        .ottz-card:hover .ottz-card-highlight {
            opacity: 1; max-height: 60px; padding-top: 12px;
        }
        .ottz-card-highlight.amazon { color: var(--signal-deep); }

        /* ---------- DEVICE SHOWCASE ---------- */
        .ottz-device { padding: 70px 6% 80px; background: var(--panel); }
        .ottz-device-tabs { display: flex; justify-content: center; gap: 6px; flex-wrap: wrap; margin: 26px 0 40px; }
        .ottz-device-tab {
            display: inline-flex; align-items: center; gap: 7px; background: var(--paper); border: 1.5px solid var(--line);
            border-bottom: 3px solid var(--line); color: var(--ink-soft); padding: 10px 18px 8px; border-radius: 8px 8px 0 0;
            font-size: 0.83rem; font-weight: 600; cursor: pointer; transition: all 0.2s ease;
        }
        .ottz-device-tab.active { color: var(--ink); border-color: var(--line); border-bottom-color: var(--signal); background: var(--paper); transform: translateY(-2px); }
        .ottz-device-stage { display: grid; grid-template-columns: 1fr; gap: 40px; align-items: center; max-width: 1000px; margin: 0 auto; opacity: 0; transform: translateY(16px); transition: all 0.6s cubic-bezier(0.16,1,0.3,1); }
        .ottz-device-stage.in-view { opacity: 1; transform: translateY(0); }
        .ottz-device-frame { display: flex; justify-content: center; }

        .ottz-frame-body.tv { width: 100%; max-width: 360px; }
        .ottz-frame-bar { background: var(--ink); height: 20px; border-radius: 8px 8px 0 0; border: 1.5px solid var(--ink); border-bottom: none; display: flex; align-items: center; justify-content: flex-end; padding-right: 10px; }
        .ottz-frame-bar span { font-family: 'IBM Plex Mono', monospace; font-size: 0.55rem; font-weight: 700; letter-spacing: 0.06em; background: var(--signal); color: var(--paper); padding: 1px 6px; border-radius: 3px; }
        .ottz-frame-body.tv .ottz-frame-screen { background: var(--surface); aspect-ratio: 16/10; border: 1.5px solid var(--ink); border-top: none; border-radius: 0 0 6px 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; padding: 20px; }
        .ottz-frame-stand { width: 70px; height: 10px; background: var(--ink); margin: 0 auto; border-radius: 0 0 4px 4px; }

        .ottz-frame-body.phone { width: 220px; }
        .ottz-frame-notch { width: 60px; height: 16px; background: var(--ink); border-radius: 0 0 10px 10px; margin: 0 auto; }
        .ottz-frame-body.phone .ottz-frame-screen { background: var(--surface); aspect-ratio: 9/17.5; border-radius: 26px; border: 6px solid var(--ink); margin-top: -16px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; padding: 20px; }

        .ottz-frame-body.parcel { width: 100%; max-width: 340px; display: flex; flex-direction: column; align-items: center; gap: 14px; }
        .ottz-frame-body.parcel .ottz-frame-screen.parcel-face { width: 100%; background: var(--paper); border: 1.5px solid var(--line); aspect-ratio: 4/3; border-radius: 10px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 20px; }
        .ottz-parcel-icon { color: var(--ink-faint); }
        .ottz-parcel-badge { display: inline-flex; align-items: center; gap: 8px; font-family: 'IBM Plex Mono', monospace; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--ink-soft); }
        .ottz-parcel-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--teal); animation: ottz-pulse 1.6s ease-in-out infinite; }

        .ottz-ad-pill { font-family: 'IBM Plex Mono', monospace; text-transform: uppercase; letter-spacing: 0.08em; background: rgba(243,246,242,0.1); border: 1px solid rgba(243,246,242,0.3); color: var(--paper); padding: 5px 12px; border-radius: 999px; font-size: 0.65rem; font-weight: 700; }
        .ottz-ad-pill.dark { background: rgba(14,27,24,0.06); border-color: var(--line); color: var(--ink); }
        .ottz-ad-slot { border: 1.5px dashed rgba(243,246,242,0.4); color: rgba(243,246,242,0.65); padding: 14px 22px; border-radius: 8px; font-weight: 700; font-size: 0.9rem; text-align: center; }
        .ottz-ad-slot.dark { border-color: var(--line); color: var(--ink-soft); }
        .ottz-audio-wave { display: flex; align-items: flex-end; gap: 3px; height: 30px; }
        .ottz-audio-wave span { width: 3px; background: var(--teal); border-radius: 2px; height: 30%; animation: ottz-wave 1.1s ease-in-out infinite; }
        @keyframes ottz-wave { 0%, 100% { height: 25%; } 50% { height: 100%; } }

        .ottz-device-copy { text-align: center; }
        .ottz-device-kicker { font-family: 'IBM Plex Mono', monospace; text-transform: uppercase; letter-spacing: 0.1em; font-size: 0.72rem; font-weight: 700; color: var(--signal-deep); }
        .ottz-device-copy h3 { font-size: 1.3rem; font-weight: 700; color: var(--ink); margin: 10px 0 20px; line-height: 1.4; }
        .ottz-device-list { list-style: none; display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; padding: 0; margin: 0; }
        .ottz-device-list li { display: inline-flex; align-items: center; gap: 6px; background: var(--paper); border: 1px solid var(--line); color: var(--ink-soft); font-size: 0.76rem; font-weight: 600; padding: 6px 12px 6px 8px; border-radius: 999px; }
        .ottz-device-list li svg { color: var(--ink-faint); }
        .ottz-device-list-code { font-family: 'IBM Plex Mono', monospace; font-size: 0.62rem; font-weight: 700; color: var(--signal-deep); background: rgba(232,98,44,0.08); padding: 2px 5px; border-radius: 4px; }

        @media (min-width: 900px) {
            .ottz-device-stage { grid-template-columns: 1fr 1fr; text-align: left; }
            .ottz-device-copy { text-align: left; }
            .ottz-device-copy h3 { margin: 10px 0 20px; }
            .ottz-device-list { justify-content: flex-start; }
        }

        /* ---------- FAQ ---------- */
        .ottz-faq { padding: 10px 6% 80px; background: var(--paper); }
        .ottz-faq-list { max-width: 760px; margin: 0 auto; display: flex; flex-direction: column; }
        .ottz-faq-item { border-bottom: 1px solid var(--line); background: transparent; transition: border-color 0.2s ease; }
        .ottz-faq-item:first-child { border-top: 1px solid var(--line); }
        .ottz-faq-q {
            width: 100%; text-align: left; background: transparent; border: none; cursor: pointer;
            padding: 18px 4px; font-size: 0.95rem; font-weight: 600; color: var(--ink);
            display: flex; align-items: center; gap: 14px;
        }
        .ottz-faq-num { font-family: 'IBM Plex Mono', monospace; font-size: 0.75rem; font-weight: 700; color: var(--ink-faint); flex-shrink: 0; }
        .ottz-faq-item.open .ottz-faq-num { color: var(--signal-deep); }
        .ottz-faq-qtext { flex: 1; }
        .ottz-faq-chevron { color: var(--ink-faint); transition: transform 0.25s ease; flex-shrink: 0; }
        .ottz-faq-item.open .ottz-faq-chevron { transform: rotate(180deg); color: var(--ink); }
        .ottz-faq-a { max-height: 0; overflow: hidden; transition: max-height 0.3s ease; }
        .ottz-faq-item.open .ottz-faq-a { max-height: 220px; }
        .ottz-faq-a p { padding: 0 4px 18px 34px; color: var(--ink-soft); font-size: 0.88rem; line-height: 1.65; }

        /* ---------- CTA BANNER (mid-page & bottom nudge) ---------- */
        .ottz-ctaband { padding: 54px 6%; background: var(--panel); border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }
        .ottz-ctaband-inner { max-width: 900px; margin: 0 auto; display: flex; flex-direction: column; gap: 22px; align-items: flex-start; text-align: left; }
        .ottz-ctaband-copy h3 { font-size: 1.4rem; font-weight: 700; color: var(--ink); margin-bottom: 8px; line-height: 1.3; }
        .ottz-ctaband-copy p { color: var(--ink-soft); font-size: 0.95rem; line-height: 1.6; max-width: 520px; }
        .ottz-ctaband .ottz-btn-primary { flex-shrink: 0; }
        @media (min-width: 768px) {
            .ottz-ctaband-inner { flex-direction: row; align-items: center; justify-content: space-between; gap: 32px; }
        }

        /* ---------- CTA / CONVERSION ---------- */
        .ottz-cta-wrap { padding: 0 6% 90px; background: var(--paper); }
        .ottz-cta { background: var(--surface); border-radius: 20px; max-width: 1000px; margin: 0 auto; padding: 40px 26px; position: relative; overflow: hidden; }
        .ottz-cta::before { content: ''; position: absolute; inset: 0; background-image: radial-gradient(circle at 1.5px 1.5px, rgba(243,246,242,0.045) 1.5px, transparent 0); background-size: 24px 24px; pointer-events: none; }
        .ottz-cta-split { position: relative; display: grid; grid-template-columns: 1fr; gap: 32px; text-align: left; }
        .ottz-cta-eyebrow { display: inline-flex; align-items: center; gap: 6px; color: var(--gold); font-family: 'IBM Plex Mono', monospace; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 14px; }
        .ottz-cta h2 { color: var(--paper); font-size: clamp(1.3rem, 4.5vw, 1.55rem); font-weight: 700; margin-bottom: 12px; }
        .ottz-cta-left p { color: rgba(243,246,242,0.65); margin-bottom: 20px; font-size: 0.95rem; line-height: 1.6; }
        .ottz-cta-points { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
        .ottz-cta-points li { display: flex; align-items: center; gap: 10px; color: rgba(243,246,242,0.88); font-size: 0.87rem; }
        .ottz-cta-points li svg { color: var(--gold); flex-shrink: 0; }

        .ottz-cta-right { background: var(--paper); border-radius: 14px; padding: 22px; position: relative; }
        .ottz-consult { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 6px; }
        .ottz-consult-icon { width: 46px; height: 46px; border-radius: 10px; background: var(--panel); border: 1.5px solid var(--signal); color: var(--signal-deep); display: flex; align-items: center; justify-content: center; margin-bottom: 6px; }
        .ottz-consult h4 { font-size: 1.05rem; font-weight: 700; color: var(--ink); margin-bottom: 2px; }
        .ottz-consult > p { color: var(--ink-soft); font-size: 0.87rem; line-height: 1.55; max-width: 320px; }
        .ottz-consult-btn { justify-content: center; width: 100%; margin-top: 10px; }
        .ottz-lead-microcopy { display: flex; align-items: center; justify-content: center; gap: 6px; color: var(--ink-faint); font-size: 0.72rem; margin: 10px 0 0; }
        @media (min-width: 420px) {
            .ottz-consult-btn { width: auto; }
        }
        @media (min-width: 900px) {
            .ottz-cta { padding: 54px 48px; }
            .ottz-cta-split { grid-template-columns: 1.1fr 1fr; align-items: center; }
            .ottz-cta h2 { font-size: 2rem; }
        }
    `;

    return (
        <div className="ottz-page">
            <Helmet>
                <title>OTT & Amazon Advertising | Prime, Spotify, ZEE5, Hotstar | BMF</title>
                <meta name="description" content="Registered OTT advertising partner offering Amazon Prime, Spotify, ZEE5, Hotstar, MX Player video/audio ads plus Amazon Courier, Pamphlet & DSP advertising across Tamil Nadu." />
                <meta name="keywords" content="OTT advertising, Amazon Prime advertising, Spotify ads, ZEE5 advertising, Hotstar advertising, Amazon DSP, Amazon courier advertisement, Amazon pamphlet advertisement, MX Player advertisement, Branding Media Factory" />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="OTT & Amazon Advertising | BMF Tamil Nadu" />
                <meta property="og:description" content="From Prime Video to Amazon parcel inserts — BMF is a registered OTT & Amazon advertising partner reaching digital consumers across every screen." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://brandingmediafactory.com/services/ott-advertising" />
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
                <link href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500..800&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600;700&display=swap" rel="stylesheet" />
            </Helmet>
            <style>{pageStyles}</style>
            <header className="header-fixed"><Header /></header>

            {/* ---------- HERO ---------- */}
            <section className="ottz-hero" onMouseMove={handleHeroMove} ref={heroRef}>
                <LiveTicker />
                <div className="ottz-hero-badge"><BadgeCheck size={15} /> Registered OTT &amp; Amazon Advertising Partner</div>
                <h1>Advertise across Amazon, Prime Video, Spotify, ZEE5 &amp; Hotstar — <span className="ottz-highlight">from one team</span>.</h1>
                <p>We plan, book and manage ad campaigns across 8 major streaming and Amazon platforms, so you get consistent brand reach without juggling separate vendors.</p>
                <div className="ottz-hero-actions">
                    <Link to="/contact" className="ottz-btn-primary"><span>Request a Consultation</span> <ArrowRight size={18} /></Link>
                    <a href="#preview" className="ottz-btn-secondary">SEE WHERE YOUR AD LANDS ↓</a>
                </div>
            </section>

            {/* ---------- INTERACTIVE DEVICE SHOWCASE ---------- */}
            <DeviceShowcase />

            <SignalDivider />

            {/* ---------- PLATFORMS / BUILDER ---------- */}
            <section className="ottz-platforms" id="platforms">
                <div className="ottz-section-head">
                    <span className="ottz-eyebrow">Where we advertise</span>
                    <h2>Where We Can Advertise Your Brand</h2>
                    <p>Filter by category to see the streaming and Amazon platforms we run campaigns on.</p>
                </div>

                <div className="ottz-tabs">
                    {CATEGORIES.map(cat => (
                        <button
                            key={cat}
                            className={`ottz-tab ${activeCategory === cat ? 'active' : ''}`}
                            onClick={() => setActiveCategory(cat)}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                <div className={`ottz-grid ${gridInView ? 'in-view' : ''}`} ref={gridRef}>
                    {visiblePlatforms.map((p, i) => {
                        const kind = p.category === 'Streaming & OTT' ? 'streaming' : 'amazon';
                        return (
                            <div
                                className={`ottz-card ${kind}`}
                                key={p.id}
                                style={{ transitionDelay: gridInView ? `${i * 0.05}s` : '0s' }}
                                onMouseMove={handleCardTilt}
                                onMouseLeave={resetCardTilt}
                            >
                                <span className={`ottz-card-tag ${kind}`}>CH {p.channel}</span>
                                <div className="ottz-card-top">
                                    <div className={`ottz-card-icon ${kind}`}>{p.icon}</div>
                                    <div>
                                        <h3>{p.name}</h3>
                                        <span className={`ottz-card-badge ${kind}`}>{p.category}</span>
                                    </div>
                                </div>
                                <p>{p.desc}</p>
                                <div className={`ottz-card-highlight ${kind}`}>{p.highlight}</div>
                            </div>
                        );
                    })}
                </div>

                <div className="ottz-stats">
                    <StatBlock value={8} label="Platforms Covered" icon={<Tv size={17} />} />
                    <StatBlock value={2} label="Core Categories" icon={<Layers size={17} />} />
                    <StatBlock value={100} suffix="%" label="Campaigns Managed by Us" icon={<ShieldCheck size={17} />} />
                    <StatBlock value={1} label="Point of Contact" icon={<Headset size={17} />} />
                </div>
            </section>

            {/* ---------- PROCESS ---------- */}
            <section className="ottz-process">
                <div className="ottz-section-head">
                    <span className="ottz-eyebrow">How it works</span>
                    <h2>How It Works</h2>
                    <p>A simple, three-step process from first conversation to a live campaign.</p>
                </div>
                <div className={`ottz-steps ${stepsInView ? 'in-view' : ''}`} ref={stepsRef}>
                    {PROCESS_STEPS.map(step => (
                        <div className="ottz-step" key={step.n}>
                            <div className="ottz-step-top">
                                <span className="ottz-step-n">{step.n}</span>
                                <span className="ottz-step-icon">{step.icon}</span>
                            </div>
                            <h3>{step.title}</h3>
                            <p>{step.detail}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ---------- MID-PAGE CTA ---------- */}
            <CtaBanner
                eyebrow="In the meantime"
                title="Not sure which platforms fit your brand?"
                subtitle="Skip the guesswork — tell us your category and budget, and we'll map out the right mix for you."
            />

            {/* ---------- BENEFITS ---------- */}
            <section className="ottz-benefits">
                <div className="ottz-section-head">
                    <span className="ottz-eyebrow">Why us</span>
                    <h2>Why Work With Us</h2>
                    <p>What you get when BMF manages your OTT & Amazon advertising.</p>
                </div>
                <div className={`ottz-benefits-grid ${benefitsInView ? 'in-view' : ''}`} ref={benefitsRef}>
                    {BENEFITS.map((b, i) => (
                        <div className="ottz-benefit" key={b.title} style={{ transitionDelay: benefitsInView ? `${i * 0.1}s` : '0s' }}>
                            <div className="ottz-benefit-icon">{b.icon}</div>
                            <div>
                                <h4>{b.title}</h4>
                                <p>{b.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ---------- FAQ ---------- */}
            <section className="ottz-faq">
                <div className="ottz-section-head">
                    <span className="ottz-eyebrow">Before you reach out</span>
                    <h2>Questions Before You Reach Out</h2>
                    <p>The things most businesses ask before starting their first campaign with us.</p>
                </div>
                <FaqAccordion />
            </section>

            {/* ---------- CTA / CONVERSION ---------- */}
            <div className="ottz-cta-wrap">
                <div className="ottz-cta ottz-cta-split">
                    <div className="ottz-cta-left">
                        <span className="ottz-cta-eyebrow"><BadgeCheck size={14} /> No-obligation consultation</span>
                        <h2>Ready to put your brand on more screens?</h2>
                        <p>Tell us your category and budget — we'll recommend the right platform mix and reply personally, not with an auto-responder.</p>
                        <ul className="ottz-cta-points">
                            <li><Check size={16} /> Registered Amazon & OTT advertising partner</li>
                            <li><Check size={16} /> One team manages all 8 platforms for you</li>
                            <li><Check size={16} /> You keep a single point of contact throughout</li>
                        </ul>
                    </div>
                    <div className="ottz-cta-right">
                        <ConsultationPanel />
                    </div>
                </div>
            </div>

            {/* ---------- BOTTOM CTA ---------- */}
            <CtaBanner
                eyebrow="Last step"
                title="Ready to get your brand in front of more viewers?"
                subtitle="Reach out and we'll walk you through the platforms, budget and timeline together."
            />

            <Footer />
        </div>
    );
};

export default OTTAdvertising;